/**
 * GRN composer form schema (string-typed, operator-facing).
 *
 * A goods receipt is recorded *against a PO*: the operator picks a
 * receivable PO, the composer pre-fills one row per PO line (ordered qty,
 * rate, description, hsn, unit). Received defaults to ordered; accepted
 * defaults to received. The wire contract (`goodsReceipts.record`) takes
 * operator decimal strings for received/accepted — so the form stays
 * string-typed and maps to `{ purchaseOrderLineId, receivedQty, acceptedQty,
 * rejectReason }` at submit time.
 *
 * @see packages/api-contract/src/procedures/goods-receipts.ts
 */
import { z } from "zod";

/** One editable line in the received-quantities table. */
export interface GrnLineRow {
  /** The PO line this row receives against (`recordLineInput.purchaseOrderLineId`). */
  purchaseOrderLineId: string;
  description: string;
  hsn: string;
  unit: string;
  /** Ordered qty (operator string) — fixed, shown read-only. */
  ordered: string;
  /** Rate in integer paise (stringified) — used for the matching-amount preview. */
  ratePaise: string;
  /** Received qty — editable. */
  received: string;
  /** Accepted qty — editable. */
  accepted: string;
  /** Free-text reason for any rejection/short. */
  rejectReason: string;
}

const grnLineFormSchema = z.object({
  purchaseOrderLineId: z.string(),
  description: z.string(),
  hsn: z.string(),
  unit: z.string(),
  ordered: z.string(),
  ratePaise: z.string(),
  received: z.string(),
  accepted: z.string(),
  rejectReason: z.string(),
});

export const grnFormSchema = z.object({
  purchaseOrderId: z.string().min(1, "Pick a purchase order"),
  receivedDate: z.string().min(1, "Received date is required"),
  vehicleChallan: z.string(),
  receivedBy: z.string(),
  notes: z.string(),
  /** Warehouse to receive into. Empty string = use company default. */
  warehouseId: z.string(),
  lines: z.array(grnLineFormSchema),
});

export type GrnFormValues = z.infer<typeof grnFormSchema>;

export const EMPTY_GRN_FORM: GrnFormValues = {
  purchaseOrderId: "",
  receivedDate: todayIso(),
  vehicleChallan: "",
  receivedBy: "",
  notes: "",
  warehouseId: "",
  lines: [],
};

/**
 * Wall-clock default for the received-date field. Acceptable in app code —
 * AGENTS.md §7 rule 12 (`no new Date()`) applies strictly to domain code;
 * UI date defaults are not part of the financial-truth surface.
 */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
